(function () {
  'use strict';

  if (window.customElements.get('comparison-table')) {
    return;
  }

  class ComparisonTable extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._loading = this.querySelector('[data-compare-loading]');
      this._frame = this.querySelector('[data-frame]');
      this._scrollers = {
        main: this.querySelector('[data-scroller="main"]'),
        technical: this.querySelector('[data-scroller="technical"]')
      };
      this._grids = {
        main: this.querySelector('[data-grid="main"]'),
        technical: this.querySelector('[data-grid="technical"]')
      };
      this._accordion = this.querySelector('[data-accordion]');
      this._indicator = this.querySelector('[data-scroll-indicator]');
      this._addTemplate = this.querySelector('template[data-add-column]');
      this._fallbackContainer = this.querySelector('[data-compare-fallback]');
      this._fallbackList = Array.from(this._fallbackContainer.querySelectorAll('[data-compare-column]'));
      this._backOverview = this.querySelector('[data-back-overview]');
      this._backFinder = this.querySelector('[data-back-finder]');
      this._fallbackActions = this.querySelector('[data-fallback-actions]');
      this._intros = {
        finder: this.querySelector('[data-intro="finder"]'),
        noresult: this.querySelector('[data-intro="noresult"]'),
        empty: this.querySelector('[data-intro="empty"]')
      };

      this._columns = new Map();
      this._order = [];
      this._descriptionsExpanded = false;
      this._rowMaps = this._buildRowMaps();

      this._accordion.addEventListener('click', () => this._toggleAccordion(), { signal });

      this.addEventListener('click', (event) => {
        const descriptionToggle = event.target.closest('[data-compare-description-toggle]');

        if (descriptionToggle) {
          const descriptionCell = descriptionToggle.closest('[data-row="description"]');

          if (descriptionCell) {
            this._setDescriptionsExpanded(descriptionCell.dataset.expanded !== 'true');
          }

          return;
        }

        const removeButton = event.target.closest('[data-compare-remove]');

        if (!removeButton) {
          return;
        }

        const cell = removeButton.closest('[data-compare-cell]');
        const key = cell && cell.dataset.key;

        if (!key) {
          return;
        }

        if (this._columns.has(key)) {
          this._removeColumn(key);
          return;
        }

        // Bestseller-fallback columns are removable too (Figma 7001-5441) —
        // purely visual, the stored compare selection is never touched.
        if (this._fallbackList) {
          const before = this._fallbackList.length;
          this._fallbackList = this._fallbackList.filter((wrapper) => wrapper.dataset.key !== key);

          if (this._fallbackList.length !== before) {
            this._renderFallback(this._fallbackMode);
          }
        }
      }, { signal });

      Object.keys(this._scrollers).forEach((group) => {
        this._scrollers[group].addEventListener('scroll', () => this._onScroll(group), { signal, passive: true });
      });

      window.addEventListener('resize', () => {
        this._updateIndicator();
        this._equalizeTitles();
        this._syncDescriptionToggles();
      }, { signal });

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this._equalizeTitles());
      }

      const params = new URLSearchParams(window.location.search);
      this._source = params.get('source') || '';
      this._init(params.get('products') || '');
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }
    }

    get _state() {
      return window.themeVariables.compareState;
    }

    _buildRowMaps() {
      const maps = { main: {}, technical: {} };

      Object.keys(this._grids).forEach((group) => {
        this._grids[group].querySelectorAll('.comparison-table__cell--label').forEach((cell) => {
          maps[group][cell.dataset.row] = cell.style.gridRow;
        });
      });

      return maps;
    }

    async _init(productsParam) {
      let requested;

      if (productsParam) {
        requested = productsParam.split(',').map((pair) => {
          const [handle, variant] = pair.trim().split(':');
          return handle ? { handle: handle, variant: variant || '' } : null;
        }).filter(Boolean);
      } else if (this._source === 'finder') {
        // Reached from the product finder with no products = genuine no-result state.
        this._renderFallback('finder-empty');
        return;
      } else {
        requested = this._state.getItems();
      }

      requested = requested.slice(0, this._state.MAX);

      if (!requested.length) {
        this._renderFallback(this._source === 'finder' ? 'finder-empty' : 'empty');
        return;
      }

      const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
      let hadTransientFailure = false;

      const wrappers = await Promise.all(requested.map((item) => {
        let url = root + '/products/' + encodeURIComponent(item.handle) + '?section_id=' + this.dataset.sectionId;

        if (item.variant) {
          url += '&variant=' + encodeURIComponent(item.variant);
        }

        return fetch(url)
          .then((response) => {
            if (response.ok) {
              return response.text();
            }

            // A 404 means the product/variant is genuinely gone — drop it. Any other
            // status (5xx, 429, …) is transient, so we must not delete the selection.
            if (response.status === 404) {
              return null;
            }

            throw new Error('transient');
          })
          .then((html) => {
            if (!html) {
              return null;
            }

            return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-compare-column]');
          })
          .catch(() => {
            hadTransientFailure = true;
            return null;
          });
      }));

      wrappers.filter(Boolean).forEach((wrapper) => {
        const key = wrapper.dataset.key;

        if (key && !this._columns.has(key) && this._columns.size < this._state.MAX) {
          this._columns.set(key, wrapper);
          this._order.push(key);
        }
      });

      if (!this._order.length) {
        // Keep the stored selection intact if nothing loaded because of a transient error.
        this._renderFallback(this._source === 'finder' ? 'finder-empty' : 'empty');
        return;
      }

      // Only persist the resolved set when every requested column was accounted for
      // (loaded or definitively gone). A transient failure must never truncate the selection.
      if (!hadTransientFailure) {
        this._syncState();
      }

      this._render();
    }

    _removeColumn(key) {
      this._columns.delete(key);
      this._order = this._order.filter((existing) => existing !== key);
      this._syncState();

      if (this._order.length) {
        this._render();
      } else {
        // The customer emptied the table themselves — this is the "empty" state,
        // not the finder "no result" state, regardless of how they arrived.
        this._renderFallback('empty');
      }
    }

    _syncState() {
      const items = this._order.map((key) => {
        const wrapper = this._columns.get(key);

        return {
          handle: wrapper.dataset.handle,
          variant: wrapper.dataset.variant,
          color: wrapper.dataset.color,
          colorId: wrapper.dataset.colorId,
          title: wrapper.dataset.title,
          url: wrapper.dataset.url,
          image: wrapper.dataset.image,
          price: wrapper.dataset.price
        };
      });

      this._state.setItems(items);

      // Finder-built selections are context-tagged so a later visit to any
      // collection page starts with clean checkboxes instead of these results.
      if (this._source === 'finder') {
        this._state.setContext('finder');
      }

      const params = new URLSearchParams(window.location.search);

      if (items.length) {
        params.set('products', items.map((item) => item.handle + ':' + item.variant).join(','));
      } else {
        params.delete('products');
      }

      const query = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : ''));
    }

    _render() {
      this.classList.remove('comparison-table--fallback');

      const wrappers = this._order.map((key) => this._columns.get(key));
      this._assemble(wrappers, wrappers.length < this._state.MAX);
      this._updateChrome(this._source === 'finder' ? 'finder' : 'products');
    }

    _renderFallback(mode) {
      this.classList.add('comparison-table--fallback');

      this._fallbackMode = mode || this._fallbackMode || (this._source === 'finder' ? 'finder-empty' : 'empty');

      const wrappers = this._fallbackList;

      if (wrappers.length) {
        this._assemble(wrappers, wrappers.length < this._state.MAX);
      } else {
        this._loading.hidden = true;
        this._frame.hidden = true;
        this._accordion.hidden = true;
        this._indicator.hidden = true;
      }

      this._updateChrome(mode || (this._source === 'finder' ? 'finder-empty' : 'empty'));
    }

    _assemble(wrappers, showAdd) {
      const wasExpanded = this._accordion.getAttribute('aria-expanded') === 'true';

      Object.keys(this._grids).forEach((group) => {
        this._grids[group].querySelectorAll('[data-compare-cell], .comparison-table__cell--add').forEach((cell) => cell.remove());
        this._grids[group].querySelectorAll('.comparison-table__cell--label').forEach((label) => {
          label.hidden = false;
        });
      });

      const totalColumns = wrappers.length + (showAdd ? 1 : 0);
      this.style.setProperty('--compare-columns', totalColumns);

      wrappers.forEach((wrapper, index) => {
        wrapper.querySelectorAll('[data-compare-cell]').forEach((cell) => {
          this._placeCell(cell.cloneNode(true), index + 2, wrapper.dataset.key);
        });
      });

      if (showAdd) {
        const fragment = this._addTemplate.content.cloneNode(true);
        const addLink = fragment.querySelector('[data-add-link]');

        if (addLink) {
          addLink.setAttribute('href', this._state.getReturnUrl(this.dataset.returnFallback));
        }

        Array.from(fragment.querySelectorAll('.comparison-table__cell')).forEach((cell) => {
          this._placeCell(cell, wrappers.length + 2, '');
        });
      }

      this._hideEmptyRows();

      // Preserve the accordion's open/closed state across re-assembly (e.g. removing a
      // column while the technical section is open). _hideEmptyRows() has already hidden
      // the accordion when no technical values remain.
      const keepExpanded = wasExpanded && !this._accordion.hidden;
      this._accordion.setAttribute('aria-expanded', keepExpanded ? 'true' : 'false');
      this._scrollers.technical.hidden = !keepExpanded;

      if (keepExpanded) {
        this._scrollers.technical.scrollLeft = this._scrollers.main.scrollLeft;
      }

      this._loading.hidden = true;
      this._frame.hidden = false;
      this._updateIndicator();
      this._equalizeTitles();
      this._setDescriptionsExpanded(this._descriptionsExpanded);
      this._syncDescriptionToggles();
    }

    // A wrapping title only grows its own column's title block; reserve the same
    // height on every column so prices and CTAs stay on one line.
    _equalizeTitles() {
      const titles = Array.from(this._grids.main.querySelectorAll('.comparison-table__product-title'));

      titles.forEach((title) => {
        title.style.minBlockSize = '';
      });

      if (titles.length < 2 || this._frame.hidden) {
        return;
      }

      const maxHeight = Math.max(...titles.map((title) => title.offsetHeight));

      titles.forEach((title) => {
        title.style.minBlockSize = maxHeight + 'px';
      });
    }

    _placeCell(cell, column, key) {
      const group = cell.dataset.group === 'technical' ? 'technical' : 'main';
      const row = this._rowMaps[group][cell.dataset.row];

      if (!row) {
        return;
      }

      cell.style.gridRow = row;
      cell.style.gridColumn = column;

      if (key) {
        cell.dataset.key = key;
      }

      this._grids[group].append(cell);
    }

    _setDescriptionsExpanded(expanded) {
      this._descriptionsExpanded = expanded;

      this._grids.main.querySelectorAll('[data-compare-description-toggle]').forEach((toggle) => {
        const cell = toggle.closest('[data-row="description"]');

        if (!cell) {
          return;
        }

        cell.dataset.expanded = String(expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
      });
    }

    _syncDescriptionToggles() {
      this._grids.main.querySelectorAll('[data-row="description"]').forEach((cell) => {
        const text = cell.querySelector('[data-compare-description-text]');
        const toggle = cell.querySelector('[data-compare-description-toggle]');

        if (!text || !toggle || cell.dataset.expanded === 'true') {
          return;
        }

        // jsdom and hidden grids report zero dimensions; preserve the control
        // until the browser has a measurable rendered cell.
        if (text.scrollHeight > 0 && text.clientHeight > 0) {
          toggle.hidden = text.scrollHeight <= text.clientHeight + 1;
        }
      });
    }

    _hideEmptyRows() {
      Object.keys(this._grids).forEach((group) => {
        Object.keys(this._rowMaps[group]).forEach((rowKey) => {
          if (rowKey === 'header') {
            return;
          }

          const valueCells = Array.from(this._grids[group].querySelectorAll('[data-compare-cell][data-row="' + rowKey + '"]'));

          if (!valueCells.length) {
            return;
          }

          const allEmpty = valueCells.every((cell) => cell.dataset.empty === 'true');

          this._grids[group].querySelectorAll('[data-row="' + rowKey + '"]').forEach((cell) => {
            cell.hidden = allEmpty;
          });
        });
      });

      const hasTechnicalValues = Array.from(this._grids.technical.querySelectorAll('[data-compare-cell]'))
        .some((cell) => cell.dataset.empty !== 'true');

      this._accordion.hidden = !hasTechnicalValues;

      if (!hasTechnicalValues) {
        this._scrollers.technical.hidden = true;
      }
    }

    _toggleAccordion() {
      const expanded = this._accordion.getAttribute('aria-expanded') === 'true';

      this._accordion.setAttribute('aria-expanded', String(!expanded));
      this._scrollers.technical.hidden = expanded;

      if (!expanded) {
        this._scrollers.technical.scrollLeft = this._scrollers.main.scrollLeft;
      }
    }

    _updateChrome(mode) {
      // The back link follows where the customer came from, not the current mode:
      // arriving via the finder always offers "Zurück zum Quiz". On the fallback
      // states the header CTA button IS "Zurück zum Quiz", so the small back link
      // would duplicate it — hide it there (Figma 7001-5288).
      const cameFromFinder = this._source === 'finder' && Boolean(this._backFinder);
      const showsFallbackCta = mode === 'finder-empty' || mode === 'empty';

      if (this._backFinder) {
        this._backFinder.hidden = !cameFromFinder || showsFallbackCta;
      }

      this._backOverview.hidden = cameFromFinder;
      this._backOverview.setAttribute('href', this._state.getReturnUrl(this.dataset.returnFallback));

      this._intros.finder.hidden = mode !== 'finder';
      this._intros.noresult.hidden = mode !== 'finder-empty';
      this._intros.empty.hidden = mode !== 'empty';

      this._fallbackActions.hidden = !cameFromFinder || !showsFallbackCta;
    }

    _onScroll(group) {
      if (this._syncingScroll) {
        return;
      }

      this._syncingScroll = true;

      const other = group === 'main' ? 'technical' : 'main';
      this._scrollers[other].scrollLeft = this._scrollers[group].scrollLeft;

      window.requestAnimationFrame(() => {
        this._syncingScroll = false;
      });

      this._updateIndicator();
    }

    _updateIndicator() {
      const scroller = this._scrollers.main;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;

      if (maxScroll < 5 || this._frame.hidden) {
        this._indicator.hidden = true;
        return;
      }

      this._indicator.hidden = false;

      const columns = parseInt(this.style.getPropertyValue('--compare-columns'), 10) || 1;

      if (this._indicator.children.length !== columns) {
        this._indicator.replaceChildren(...Array.from({ length: columns }, () => {
          const segment = document.createElement('span');
          segment.className = 'comparison-table__indicator-segment';
          return segment;
        }));
      }

      const activeIndex = Math.min(columns - 1, Math.round((scroller.scrollLeft / maxScroll) * (columns - 1)));

      Array.from(this._indicator.children).forEach((segment, index) => {
        segment.classList.toggle('is-active', index === activeIndex);
      });
    }
  }

  window.customElements.define('comparison-table', ComparisonTable);
})();
